<template>
  <widget-button-component
    :style="cssVars"
    :class="{ [positionsClass[app.visual?.button_position as number]]: app.visual?.button_position }"
    @click="openForm"
    @close="closeWidget"
    v-show="!app.form.open"
    v-if="!app.forceClose && appStore().activeLink && app.externalAppLoaded"
  />
  <app-component
    :style="cssVars"
    :class="{ [positionsClass[app.visual?.button_position as number]]: app.visual?.button_position }"
    @close="closeApp"
    v-show="app.form.open"
    v-if="!app.forceClose && appStore().activeLink && app.form.hadInteraction"
  />
</template>

<script setup lang="ts">
const WIDGET_URL = import.meta.env.VITE_WIDGET_URL;
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import { getConfigurations } from '@/api/Configuration';
import { getTextColor, delay } from '@/lib/Utils';
import WidgetButtonComponent from '@/components/WidgetButtonComponent.vue';
import AppComponent from './AppComponent.vue';
import { appStore, stepStore } from '@/store/app';
import type { IConfig } from './forms/interface/IConfig';
import type { IVisualSetting } from './forms/interface/IVisualSetting';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  rbclicod?: string,
  course?: string,
  campus?: string,
  period?: string
}>();

const emits = defineEmits(['close', 'minimize']);

const app = reactive<{
  visual?: IVisualSetting,
  forceClose: boolean,
  form: {
    open: boolean,
    hadInteraction: boolean,
  },
  externalAppLoaded?: boolean
}>({
  forceClose: false,
  form: {
    open: false,
    hadInteraction: false
  }
});

const positionsClass: any = {
  1: 'top-right',
  2: 'bottom-right',
  3: 'top-left',
  4: 'bottom-left'
};

onBeforeMount(async () => {
  await getConfig();
  start();
});

watch(() => appStore().activeLink, () => {
  if (!appStore().activeLink) {
    reset();
  }
});

function start() {
  if (appStore().getConfig()?.visual.id && props.rbclicod) {
    insertStyles(() => {
      formatPsLinks();
      setNavigationEvent();
      checkUrlValid();
    });
  }
}

function closeWidget() {
  app.forceClose = true;
  stepStore().reset();
  appStore().setGlobal(null);
  emits('close');
}

function closeApp() {
  app.form.open = false;
  app.externalAppLoaded = true;
  emits('minimize');
}

function checkUrlValid(event?: any) {
  const href = (event ? event?.destination.url : window.location.href).split('#')[0];
  if (appStore().activeLink?.url !== href) {
    const link = appStore().getConfig()?.links.find((link) => link.url === href);
    appStore().setActiveLink(link);
    if (appStore().activeLink?.app_id === 1) {
      handleAppIMLoaded();
    } else {
      app.externalAppLoaded = true;
    }
  }
}

function formatPsLinks() {
  const links = appStore().getConfig()?.links ?? [];
  links.forEach((link: any) => {
    if (link.app_id === 1) {
      link.url += '/portal-inscrito';
    }
  });
}

async function handleAppIMLoaded() {
  app.externalAppLoaded = appIMLoaded();
  if (app.externalAppLoaded) return;

  delay(1000).then(handleAppIMLoaded);
}

function appIMLoaded(): boolean {
  return !Boolean(document.querySelector('app-client-loader')?.children.length);
}

function setNavigationEvent() {
  if (navigation()) {
    navigation().addEventListener('navigate', (event) => checkUrlValid(event));
  } else {
    setInterval(checkUrlValid, 300);
  }
}

function navigation() {
  return (window as any).navigation;
}

function openForm() {
  setTimeout(() => {
    appStore().setGlobal({ ...props });
    app.form.open = true;
    app.form.hadInteraction = true;
  }, 0);
}

function reset() {
  closeApp();
  closeWidget();
  app.forceClose = false;
  app.form.open = false;
  app.form.hadInteraction = false;
}

async function getConfig() {
  try {
    const data: IConfig = await getConfigurations({ rbclicod: props.rbclicod });
    appStore().setConfig(data);
    app.visual = appStore().getConfig()?.visual;
  } catch (error) { }
}

function insertStyles(callback) {
  const fonts = createStyleFont();
  const componentStyles = createComponentStyles();
  componentStyles.onload = callback;
  document.head.appendChild(fonts);
  document.querySelector('rb-pravaler')?.shadowRoot?.appendChild(componentStyles);
  if (!isShadowRoot()) {
    callback();
  }
}

function isShadowRoot() {
  return Boolean(document.querySelector('rb-pravaler')?.shadowRoot);
}

function createStyleFont() {
  const font = document.createElement('style');
  font.setAttribute('id', 'font-rb-pravaler');
  font.innerText = '@import url(\'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&display=swap\');';
  return font;
}

function createComponentStyles() {
  const styles = document.createElement('link');
  styles.setAttribute('id', 'style-rb-pravaler');
  styles.setAttribute('rel', 'stylesheet');
  styles.setAttribute('href', WIDGET_URL + 'style.css');
  return styles;
}

const cssVars = computed(() => {
  return {
    ['--primary']: appStore().getConfig()?.visual.primary_color,
    ['--secondary']: appStore().getConfig()?.visual.secondary_color,
    ['--contrast-text-primary']: getTextColor(appStore().getConfig()?.visual.primary_color ?? ''),
    ['--contrast-text-secondary']: getTextColor(appStore().getConfig()?.visual.secondary_color ?? '')
  };
});
</script>

<styles lang="scss" scoped>
* {
  --border: rgb(145, 145, 145);
  --text: rgba(0, 0, 0, 0.6);
  --input: rgba(0, 0, 0, 0.04);
  --input-focus: rgba(0, 0, 0, .07);
  --success: #2ED47A;
  --warning: #FFB946;
  --error: #F7685B;

  --shadow: rgba(0, 0, 0, 0.85);
  --shadow-1-4: rgba(0, 0, 0, 0.14);
  --shadow-1-2: rgba(0, 0, 0, 0.12);
  --shadow-2: rgba(0, 0, 0, 0.2);
  font-family: 'Poppins', 'Segoe UI';
  scrollbar-color: #A2A2A2 #D4D4D4;

  &::-webkit-scrollbar {
    width: 0.5rem;
    height: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #D4D4D4;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #A2A2A2;
  }
}

.flex-column {
  display: flex;
  flex-direction: column;
}

.flex-row {
  display: flex;
  flex-direction: row;
}

.bold {
  font-weight: bold !important;
}

.capitalize {
  text-transform: capitalize;
}

.top-right {
  top: 88px;
  right: 0;
  bottom: unset;
  left: unset;
  margin: 0 22px;
}

.top-left {
  top: 88px;
  bottom: unset;
  right: unset;
  left: 0;
  margin: 0 22px;
}

.bottom-right {
  top: unset;
  bottom: 44px;
  right: 0;
  left: unset;
  margin: 0 22px;
}

.bottom-left {
  top: unset;
  bottom: 44px;
  right: unset;
  left: 0;
  margin: 0 22px;
}
</styles>